// The waveform display's contract (P2.6) — Architecture §11 (amended, ADR-L9), UX §3.1, §5.4.
//
// **The waveform gets its own canvas and its own worker, decided by the stakeholder before this
// prompt was built** (P2.6 flags it as "an open question for the stakeholder, not for the prompt to
// decide silently"). The argument that settled it: Architecture §6.3 gives the visual scene a
// quality governor that deliberately drops the scene's frame rate under load. That is correct for
// decoration and catastrophic for an instrument — sharing the render worker would mean a heavy
// particle effect throttling the playhead. A data display must not degrade in service of a
// decoration, so it does not share a frame budget with one.
//
// The cost is honest and accepted: a second WebGL2 context and a second worker.
//
// Geometry lives here rather than in the renderer because **two threads have to agree on it.** The
// worker draws the ribbon and the overview into one canvas at these proportions; the main thread
// maps a click's y-coordinate back to whichever of them was hit, because UX §5.4 gives them
// opposite seek rules. A second copy of these fractions is a click that seeks the wrong thing.

import type { SignalSourceIndex } from './rings';

/**
 * How the lane is divided, as fractions of the canvas height, top to bottom.
 *
 * UX §3.1 stacks "Deck A scrolling ribbon" over "A overview strip" inside a ~130 px lane. The
 * ribbon takes the great majority because it is what a DJ actually reads while mixing; the overview
 * is a map, not an instrument.
 */
export const WAVEFORM_LAYOUT = {
  /** The scrolling ribbon with the centre playhead. */
  RIBBON_TOP: 0,
  RIBBON_HEIGHT: 0.72,
  /** Dead space, so the two displays read as two things rather than one tall smear. */
  GAP_HEIGHT: 0.06,
  /** The whole-track overview strip. */
  OVERVIEW_TOP: 0.78,
  OVERVIEW_HEIGHT: 0.22,
} as const;

/**
 * The same lane, upside down — overview on top, ribbon below (P3.13).
 *
 * **Deck A uses this, and the reason is UX §3.1's parenthesis rather than symmetry.** §3.1 stacks
 * "Deck A scrolling ribbon" over "Deck B scrolling ribbon **(phase-aligned under A)**", and the
 * whole value of that arrangement is that the two ribbons are *adjacent*: beat lines up under beat,
 * and a flam is something you can see. Two unmodified lanes stacked would put A's overview strip
 * between them — a map wedged between the two instruments it is a map of, breaking the one
 * comparison the layout exists to make.
 *
 * So deck A's lane is mirrored and deck B's is not, giving overview · ribbon · ribbon · overview
 * from top to bottom. That is also what every hardware-derived DJ layout does, for the same reason.
 */
export const WAVEFORM_LAYOUT_MIRRORED = {
  OVERVIEW_TOP: 0,
  OVERVIEW_HEIGHT: 0.22,
  GAP_HEIGHT: 0.06,
  RIBBON_TOP: 0.28,
  RIBBON_HEIGHT: 0.72,
} as const;

/** Which way up a lane is drawn. `mirrored` is deck A's; see `WAVEFORM_LAYOUT_MIRRORED`. */
export type WaveformOrientation = 'normal' | 'mirrored';

export interface WaveformLaneLayout {
  readonly RIBBON_TOP: number;
  readonly RIBBON_HEIGHT: number;
  readonly OVERVIEW_TOP: number;
  readonly OVERVIEW_HEIGHT: number;
}

export function layoutFor(orientation: WaveformOrientation): WaveformLaneLayout {
  return orientation === 'mirrored' ? WAVEFORM_LAYOUT_MIRRORED : WAVEFORM_LAYOUT;
}

/**
 * The beatgrid the ribbon draws ticks from (P3.13) — UX §3.1's "beat ticks".
 *
 * **The grid the deck is actually using**, which since P3.12 may be a human's correction rather
 * than the detector's answer. That is the point of drawing it at all: FR-13 gives the user tools to
 * move a grid and, until now, nothing to see the result on. P3.5's owed check — "does the grid line
 * up?" — is not answerable by a number, only by looking at where the lines fall against the
 * transients.
 *
 * Seconds, matching `SyncGrid`, so nothing has to be rescaled when the device rate changes.
 */
export interface WaveformGrid {
  readonly anchorSeconds: number;
  readonly beatSeconds: number;
  /**
   * Whether to draw a brighter line every fourth beat.
   *
   * A bar line is what makes a grid readable at a glance — sixteen identical ticks in the ribbon is
   * a comb, not a grid. **Bar one is the anchor**, which is a claim v1 is not entitled to make in
   * general (nothing detects downbeats yet — Architecture §5 stops at a constant-tempo grid), so
   * this is off unless a human has placed the anchor themselves.
   */
  readonly showBars: boolean;
}

/**
 * How much of the track the ribbon shows, in seconds.
 *
 * Eight seconds is roughly sixteen beats at 120 BPM — enough to see the phrase you are mixing into
 * and the one you just left, which is what the ribbon is for. It is also, deliberately, **not** the
 * tightest zoom P2.4's ladder was designed for: that derivation sized the finest mipmap level for a
 * ~2 s zoom, which M2 has no control to reach. Level 0 therefore goes unused until a zoom control
 * lands (M3+), which is the ladder having headroom rather than the ladder being wrong.
 */
export const RIBBON_VISIBLE_SECONDS = 8;

/**
 * Colours, resolved from CSS custom properties on the main thread and passed in.
 *
 * The worker cannot read `--deck-a` itself: there is no DOM in a worker, and there is no
 * `getComputedStyle`. Passing the resolved values keeps UX §5.1's rule intact — "UI tokens are
 * role-based, never raw colors in components" — because the *component* still asks for a token by
 * name; only the resolution happens on the thread that has a stylesheet. A hex literal in the
 * worker would silently opt the waveform out of every future theme (M7).
 *
 * Components are 0–1 RGB, which is what GLSL wants, so nothing converts per frame.
 */
export interface WaveformTheme {
  /** The min/max envelope — the deck's identity colour. */
  readonly envelope: readonly [number, number, number];
  /** The RMS core, drawn brighter inside the envelope. */
  readonly core: readonly [number, number, number];
  readonly playhead: readonly [number, number, number];
}

export interface WaveformInitMessage {
  readonly type: 'init';
  readonly canvas: OffscreenCanvas;
  /** Same layout as the render worker's, so `/bench` reads both with one function (P2.6's verify
   * measures the ribbon *and* the scene together). */
  readonly stats: SharedArrayBuffer;
  readonly width: number;
  readonly height: number;
  readonly pixelRatio: number;
  readonly theme: WaveformTheme;
  /**
   * Which source block in the FR-26 signal frame this lane's playhead comes from (P3.13).
   *
   * ⚠️ **Was hard-coded to `DECK_A` in the renderer until this prompt**, which was correct while
   * there was one lane and becomes a lane drawing the *wrong deck's* playhead the moment there are
   * two. Sent at init rather than with each track because it is a property of the lane, not of what
   * is loaded into it.
   */
  readonly sourceIndex: SignalSourceIndex;
  /** Deck A's lane is drawn upside down so the two ribbons meet — see `WAVEFORM_LAYOUT_MIRRORED`. */
  readonly orientation: WaveformOrientation;
}

/**
 * A track arrived. Both buffers are shared, never transferred.
 *
 * `mipmaps` is P2.4's output and is read-only here. `signals` is the FR-26 ring the **audio thread**
 * publishes into — Architecture §7's rule that the UI "renders these; it never computes transport
 * time from its own clocks". A waveform that advanced its own playhead from a rAF timestamp would
 * drift against the audio within seconds and be wrong in the one way a DJ would immediately feel.
 *
 * **The ring, not `DECK_STATUS`, since P2.9.** The playhead was read from the deck's status buffer
 * at P2.6 because that was the only place it existed. Now that the deck publishes a proper source
 * block, reading from the ring means the position the waveform draws and the level a meter shows
 * come from the *same frame* — they describe one block of audio rather than two moments that happen
 * to be close.
 */
export interface WaveformTrackMessage {
  readonly type: 'track';
  readonly mipmaps: SharedArrayBuffer;
  readonly signals: SharedArrayBuffer;
  readonly frameCount: number;
  readonly sampleRateHz: number;
}

/**
 * The beatgrid changed (P3.13) — a new track, a re-analysis, or one of FR-13's manual corrections.
 *
 * **Its own message rather than a field on `track`**, because the two change at different times and
 * for different reasons: a track arrives once, and its grid can then be corrected a dozen times by a
 * hand on the tap button without a byte of audio moving. Folding the grid into `track` would mean
 * re-sending — and re-uploading — several megabytes of mipmap to move a line by ten milliseconds.
 */
export interface WaveformGridMessage {
  readonly type: 'grid';
  /** Null when the track has no usable grid. The ribbon then draws no ticks, which is the honest
   * rendering of "no grid" and is distinguishable from ticks in the wrong place. */
  readonly grid: WaveformGrid | null;
}

/** The deck was emptied. Drops the textures rather than leaving the last track on screen. */
export interface WaveformClearMessage {
  readonly type: 'clear';
}

export interface WaveformResizeMessage {
  readonly type: 'resize';
  readonly width: number;
  readonly height: number;
  readonly pixelRatio: number;
}

export interface WaveformDisposeMessage {
  readonly type: 'dispose';
}

export type WaveformWorkerMessage =
  | WaveformInitMessage
  | WaveformTrackMessage
  | WaveformGridMessage
  | WaveformClearMessage
  | WaveformResizeMessage
  | WaveformDisposeMessage;

/** Which display a pointer landed on. UX §5.4 gives them opposite seek rules, so the distinction
 * is behavioural rather than cosmetic. */
export type WaveformRegion = 'ribbon' | 'overview' | 'none';

/**
 * Which display contains a click, from its y position within the lane (0 = top, 1 = bottom).
 *
 * Here rather than in the component so it can be tested without a DOM, and so the mapping is
 * expressed exactly once against the constants the worker draws from.
 */
export function regionAt(
  yFraction: number,
  orientation: WaveformOrientation = 'normal',
): WaveformRegion {
  // ⚠️ **Takes the orientation, and must.** Deck A's lane is drawn mirrored since P3.13, so a
  // hard-coded reading of `WAVEFORM_LAYOUT` here would map the top of that lane — its *overview* —
  // to `'ribbon'`, and UX §5.4 gives the two opposite seek rules. The bug would be a click on deck
  // A's overview refusing to seek while playing, which is precisely the needle-drop protection
  // applying to the one display that is supposed to be exempt from it.
  const layout = layoutFor(orientation);
  // Both bounds inclusive, and the ribbon's used to be exclusive — which was invisible while the
  // ribbon was never the bottom region and became a dead last pixel row the moment deck A's lane
  // was flipped. The two regions stay disjoint because the gap between them is real in either
  // orientation, so inclusivity cannot make a click ambiguous.
  if (
    yFraction >= layout.RIBBON_TOP &&
    yFraction <= layout.RIBBON_TOP + layout.RIBBON_HEIGHT
  ) {
    return 'ribbon';
  }
  if (
    yFraction >= layout.OVERVIEW_TOP &&
    yFraction <= layout.OVERVIEW_TOP + layout.OVERVIEW_HEIGHT
  ) {
    return 'overview';
  }
  // The gap between them, or outside the lane. Deliberately not "nearest": a click in the dead
  // space is an unaimed click, and guessing which display the user meant is how a needle-drop
  // accident happens.
  return 'none';
}

/**
 * Does a click on this region seek, given the transport state? **UX §5.4, exactly.**
 *
 * "Overview strip always seeks on click; scrolling ribbon seeks only while paused (live
 * needle-drop accidents are unforgivable)."
 *
 * The asymmetry is the whole rule and it is worth restating why, because it looks like an
 * inconsistency to anyone who has not done it: the overview is a map, so clicking it is navigation
 * and a DJ who clicks it means it. The ribbon is directly under the pointer during a mix, at the
 * exact moment hands are moving fast — and a stray click there while a track is playing to a room
 * is an audible jump nobody can undo.
 */
export function seekAllowed(region: WaveformRegion, playing: boolean): boolean {
  if (region === 'overview') {
    return true;
  }
  if (region === 'ribbon') {
    return !playing;
  }
  return false;
}
